import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { configs } from '@constants/configs/configs';
import { useAppSelector } from '@hooks/index';
import { Tab, TabData } from './Tab';
import { SideNav } from 'app-components';
import { BREAK_POINTS } from '@constants/common/breakpoints';

const StyledSidebarContainer = styled.div<React.ComponentPropsWithRef<'div'>>`
    width: 25%;
    border: none;
    background-color: transparent;
    .gi-sidebar_nav_list {
        background-color: transparent;
        font-weight: 400;
        padding: 0px
    }
    @media (max-width: ${BREAK_POINTS.sm}) {
        width: 100%;
    }
`;

const StyledHeader = styled.div`
    background-color: transparent;
    margin: 0;
    border-left: none;
    border-right: none;
    padding: 16px 0px;
    .heading {
        margin-bottom: 0;
        font-size: 18px;
    }
`;

const Sidebar = () => {
    const {
        routes: {
            RIDP,
            RIDP_CONTACT_INFO,
            RIDP_UPLOAD_DOCUMENT,
            RIDP_VERIFICATION_COMPLETE,
            RIDP_CALL_EXPERIAN,
            RIDP_IDENTITY_QUESTION,
            RIDP_TECHNICAL_ERROR,
            RIDP_UPLOAD_DOCUMENT_PROCESS,
        },
    } = configs;

    const intl = useIntl();

    const location = useLocation();

    const isRidpRbaEnabled = useAppSelector(state => state.ridp.currentUserInfo?.isRidpRbaEnabled === true);

    const tabData: TabData[] = [
        { id: '1', path: RIDP, messageId: 'ridp.sidebar.getStarted' },
        {
            id: '2',
            path: RIDP_CONTACT_INFO,
            messageId: 'ridp.sidebar.contactInformation',
        },
        {
            id: '3',
            path: RIDP_IDENTITY_QUESTION,
            messageId: 'ridp.sidebar.identityQuestions',
        },
        {
            id: '4',
            path: RIDP_CALL_EXPERIAN,
            messageId: 'ridp.sidebar.callExperian',
        },
        {
            id: '5',
            path: RIDP_UPLOAD_DOCUMENT,
            messageId: 'ridp.sidebar.manualVerification',
        },
        {
            id: '6',
            path: RIDP_UPLOAD_DOCUMENT_PROCESS,
            messageId: 'ridp.sidebar.manualVerification',
        },
        {
            id: '7',
            path: RIDP_VERIFICATION_COMPLETE,
            messageId: 'ridp.sidebar.finish',
        },
        {
            id: '8',
            path: RIDP_TECHNICAL_ERROR,
            messageId: 'ridp.sidebar.finish',
        },
    ];

    const conditionalPaths = [
        RIDP_CALL_EXPERIAN,
        location.pathname === RIDP_TECHNICAL_ERROR ? RIDP_VERIFICATION_COMPLETE : RIDP_TECHNICAL_ERROR,
        location.pathname !== RIDP_UPLOAD_DOCUMENT_PROCESS ? RIDP_UPLOAD_DOCUMENT_PROCESS : null,
        location.pathname !== RIDP_UPLOAD_DOCUMENT ? RIDP_UPLOAD_DOCUMENT : null,
    ];

    const filteredTabData = tabData.filter(tab => {
        if (isRidpRbaEnabled && tab.path === RIDP_IDENTITY_QUESTION) {
            return false;
        }
        // Include tabs with conditional paths if they match the current route
        if (conditionalPaths.includes(tab.path) && location.pathname === tab.path) {
            return true;
        }

        // Include tabs that are not in the conditional paths
        return !conditionalPaths.includes(tab.path);
    });

    return (
        <StyledSidebarContainer
            className="ridp-side-nav"
            role="navigation"
            aria-label={intl.formatMessage({ id: 'ridp.sidebar.header' })}
        >
            <StyledHeader>
                <span className="h3 heading">
                    <FormattedMessage id="ridp.sidebar.header" />
                </span>
            </StyledHeader>
            <SideNav.Item>
                {filteredTabData.map((tab, _index) => (
                    <Tab key={tab.id} active={location.pathname === tab.path} ariaCurrent="page">
                        <FormattedMessage id={tab.messageId} />
                    </Tab>
                ))}
            </SideNav.Item>
        </StyledSidebarContainer>
    );
};

export default Sidebar;
